import { fazerLogout } from '../utils/logout';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import '../styles/Pedidos.css';
import '../styles/Dashboard.css';
import '../styles/ConsultaPedido.css';
import Logo from './Logo';
import Desdobramento from './modals/Desdobramento';
import { getAuthSession } from '../utils/authStorage';

// Mapeamento de status da API Winthor
const STATUS_MAP = {
  P: { texto: 'PENDENTE',   classe: 'status-pendente', cor: '#f59e0b', bg: '#fffbeb' },
  M: { texto: 'PENDENTE',   classe: 'status-pendente', cor: '#f59e0b', bg: '#fffbeb' },
  B: { texto: 'BLOQUEADO',  classe: 'status-cancelado', cor: '#ef4444', bg: '#fef2f2' },
  C: { texto: 'CANCELADO',  classe: 'status-cancelado', cor: '#dc2626', bg: '#fef2f2' },
  F: { texto: 'CONCLUÍDO',  classe: 'status-entregue', cor: '#10b981', bg: '#ecfdf5' },
  L: { texto: 'CONCLUÍDO',  classe: 'status-entregue', cor: '#10b981', bg: '#ecfdf5' },
};

// Mapeamento de origem do pedido
const SALE_ORIGIN_MAP = {
  B: 'Balcão',
  R: 'Balcão Reserva',
  K: 'Venda Broker',
  C: 'Callcenter',
  F: 'Força de Venda',
  T: 'Telemarketing',
  W: 'Venda WEB',
  A: 'Auto Serviço (Varejo)',
};

// Mapeamento de tipo de venda
const SALE_TYPE_MAP = {
  1: 'Venda normal',
  5: 'Bonificação ou brinde',
  7: 'Venda para Entrega Futura',
  8: 'Simples Remessa de Venda para Entrega Futura',
};

// Mapeamento de plano de pagamento
const PAYMENT_PLAN_MAP = {
  1: 'À Vista',
  10: '30/60 Dias',
  13: '30/60/90 Dias',
  30: '30 Dias',
  64: 'Plano Negocial',
  70: 'Débito',
  71: 'Crédito à Vista',
  101: '3X',
  106: '8X',
  111: 'PFUN - À Vista',
  112: 'PFUN - 2X',
  113: 'PFUN - 3X',
};

// Cache com TTL (5 minutos)
const CACHE_TTL = 5 * 60 * 1000; // 5 min em ms

function salvarCacheDesdobramento(orderId, data) {
  try {
    const cache = JSON.parse(sessionStorage.getItem('desdobramentoCache') || '{}');
    cache[orderId] = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem('desdobramentoCache', JSON.stringify(cache));
  } catch {
    // Ignora erros de storage
  }
}

function obterCacheDesdobramento(orderId) {
  try {
    const cache = JSON.parse(sessionStorage.getItem('desdobramentoCache') || '{}');
    const entry = cache[orderId];
    if (!entry) return null;
    
    const idade = Date.now() - entry.timestamp;
    if (idade > CACHE_TTL) {
      // Cache expirou, remove
      delete cache[orderId];
      sessionStorage.setItem('desdobramentoCache', JSON.stringify(cache));
      return null;
    }
    
    return entry.data;
  } catch {
    return null;
  }
}

function getStatusInfo(code) {
  return STATUS_MAP[code] || { texto: code || 'DESCONHECIDO', classe: '', cor: '#6b7280', bg: '#f9fafb' };
}

function getStatus(code) {
  return STATUS_MAP[code] || { texto: code || '—', cor: '#6b7280', bg: '#f9fafb' };
}

function getSummaryStatus(detail) {
  if (!detail || !detail.desdobramento || !Array.isArray(detail.desdobramento.resumoPorProduto)) {
    return null;
  }

  const allComplete = detail.desdobramento.resumoPorProduto.every((item) => {
    const restante = Number(item.qtdRestante ?? 0);
    const pedida = Number(item.qtdPedida ?? 0);
    const desdobrada = Number(item.qtdDesdobrada ?? 0);
    const percentual = pedida > 0 ? Math.min(100, Math.round((desdobrada / pedida) * 100)) : 0;
    return restante <= 0 || percentual >= 100;
  });

  return allComplete
    ? { texto: 'CONCLUÍDO', classe: 'status-entregue', cor: '#10b981', bg: '#ecfdf5' }
    : { texto: 'PENDENTE',  classe: 'status-pendente', cor: '#f59e0b', bg: '#fffbeb' };
}

function getEffectiveStatus(pedido) {
  const summary = getSummaryStatus(pedido);
  if (summary) return summary;
  return getStatusInfo(pedido.orderStatus);
}

function getEffectiveStatusClass(pedido) {
  const summary = getSummaryStatus(pedido);
  if (summary) {
    return summary.texto === 'CONCLUÍDO' ? 'concluido' : 'pendente';
  }
  return String(pedido.orderStatus || '').toLowerCase();
}

function getPaymentPlan(code) {
  return PAYMENT_PLAN_MAP[code] || code || '—';
}

function formatarMoeda(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarData(d) {
  if (!d) return 'N/A';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
}

function formatarMesAno(dataStr) {
  if (!dataStr) return '';
  const [ano, mes] = dataStr.split('-');
  const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return `${meses[parseInt(mes) - 1]} de ${ano}`;
}

function extractSaleType(p) {
  if (!p) return null;
  const candidates = [p.saleType, p.sale_type, p.saleTypeId, p.saleTypeID, p.saleTypeCode, p.tipoVenda, p.type, p.orderType, p.sale_type_code];
  for (const c of candidates) {
    if (c !== undefined && c !== null) return String(c).trim();
  }
  return null;
}

function Pedidos() {
  const [pedidos,         setPedidos]         = useState([]);
  const [filtroStatus,    setFiltroStatus]    = useState('todos');
  const [buscaTexto,      setBuscaTexto]      = useState('');
  const [ordenacao,       setOrdenacao]       = useState('mais-recentes');
  const [clienteNome,     setClienteNome]     = useState('');
  const [pedidoDetalhado, setPedidoDetalhado] = useState(null);
  const [pedidoFilhoSelecionado, setPedidoFilhoSelecionado] = useState(null);
  const [pedidoDesdobramento, setPedidoDesdobramento] = useState(null);
  const [statusResumo, setStatusResumo] = useState({});
  const [carregando,      setCarregando]      = useState(true);
  const [carregandoResumo, setCarregandoResumo] = useState(false);
  const [carregandoDetalhes, setCarregandoDetalhes] = useState(false);
  const [pedidoCarregandoDetalhesId, setPedidoCarregandoDetalhesId] = useState(null);
  const [paginaAtual,     setPaginaAtual]     = useState(1);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const PEDIDOS_POR_PAGINA = 12;

  const getPedidoKey = (pedido) => String(pedido.orderId ?? pedido.idPedido ?? pedido.id ?? '').trim();

  const getPedidoEffectiveStatus = (pedido) => {
    const pedidoKey = getPedidoKey(pedido);
    const resumoEntrada = statusResumo[pedidoKey];

    if (resumoEntrada && resumoEntrada.loading) {
      return {
        texto: 'CARREGANDO...',
        classe: 'status-loading',
        cor: '#6b7280',
        bg: '#f3f4f6',
        loading: true,
      };
    }

    if (resumoEntrada) {
      return resumoEntrada;
    }

    return getStatusInfo(pedido.orderStatus);
  };

  useEffect(() => {
    const { token, customerId, customerName } = getAuthSession();

    if (!customerId || !token) { navigate('/'); return; }
    setClienteNome(customerName);

    // Busca todos os pedidos dos últimos 6 meses (sem filtro de data específico)
    // Não enviar CNPJ: o JWT já identifica o cliente no servidor
    fetch('/api/orders/by-cnpj', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    })
      .then(r => r.json())
      .then(data => {
        console.log('[PEDIDOS] Dados recebidos:', data);
        if (data.ok && data.orders) {
          console.log('[PEDIDOS] Total de pedidos (antes do filtro):', data.orders.length);
          const allTypes = Array.from(new Set(data.orders.map(p => extractSaleType(p)).filter(Boolean)));
          console.log('[PEDIDOS] saleType values presentes:', allTypes);

          const filtered = data.orders.filter(p => {
            const st = extractSaleType(p);
            return st === '7';
          });
          console.log('[PEDIDOS] Total de pedidos (após filtrar apenas saleType 7):', filtered.length);
          setPedidos(filtered);
        }
      })
      .catch((err) => console.error('[PEDIDOS] Erro:', err))
      .finally(() => setCarregando(false));
  }, [navigate]);

  // Aplica filtro vindo do Dashboard via query string
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f) setFiltroStatus(f);
  }, [searchParams]);

  const pedidosFiltrados = (() => {
    console.log('[PEDIDOS] Filtro atual:', filtroStatus);
    console.log('[PEDIDOS] Total antes do filtro:', pedidos.length);

    const termoBusca = buscaTexto.trim().toLowerCase();
    const filtrados = pedidos.filter((pedido) => {
      const status = getPedidoEffectiveStatus(pedido);
      const atendeStatus = filtroStatus === 'todos'
        ? true
        : filtroStatus === 'F'
          ? status.texto === 'CONCLUÍDO'
          : filtroStatus === 'M'
            ? status.texto === 'PENDENTE'
            : status.texto === getStatusInfo(filtroStatus).texto;

      if (!atendeStatus) return false;
      if (!termoBusca) return true;

      const numeroPedido = String(pedido.orderId || pedido.idPedido || '').toLowerCase();
      const notaFiscal = String(pedido.notaFiscal || pedido.numeroNotaFiscal || '').toLowerCase();
      return numeroPedido.includes(termoBusca) || notaFiscal.includes(termoBusca);
    });

    const ordenacaoFinal = [...filtrados];
    ordenacaoFinal.sort((a, b) => {
      if (ordenacao === 'maior-valor') {
        return Number(b.totalValue || 0) - Number(a.totalValue || 0);
      }
      if (ordenacao === 'menor-valor') {
        return Number(a.totalValue || 0) - Number(b.totalValue || 0);
      }
      const dataA = new Date(a.createData || a.orderDate || 0);
      const dataB = new Date(b.createData || b.orderDate || 0);
      return dataB - dataA;
    });

    console.log('[PEDIDOS] Total após filtro:', ordenacaoFinal.length);
    return ordenacaoFinal;
  })();

  // Paginação
  const totalPaginas = Math.ceil(pedidosFiltrados.length / PEDIDOS_POR_PAGINA);
  const indiceInicio = (paginaAtual - 1) * PEDIDOS_POR_PAGINA;
  const indiceFim = indiceInicio + PEDIDOS_POR_PAGINA;
  const pedidosDaPagina = pedidosFiltrados.slice(indiceInicio, indiceFim);

  useEffect(() => {
    const { token } = getAuthSession();
    if (!token || pedidos.length === 0) return;

    const carregarStatusResumo = async () => {
      setCarregandoResumo(true);
      
      // Tenta carregar do cache primeiro
      const cachedResumo = sessionStorage.getItem('statusResumo');
      let initialMap = {};
      
      if (cachedResumo) {
        try {
          const cached = JSON.parse(cachedResumo);
          // Converte o cache para o formato esperado
          Object.entries(cached).forEach(([key, value]) => {
            if (value === 'PENDENTE' || value === 'CONCLUÍDO') {
              // Converte de string para objeto de status
              initialMap[key] = value === 'CONCLUÍDO' 
                ? { texto: 'CONCLUÍDO', classe: 'status-entregue', cor: '#10b981', bg: '#ecfdf5' }
                : { texto: 'PENDENTE', classe: 'status-pendente', cor: '#f59e0b', bg: '#fffbeb' };
            } else if (value && typeof value === 'object') {
              initialMap[key] = value;
            }
          });
          setStatusResumo(initialMap);
        } catch {
          // Ignora cache inválido
        }
      }

      // Requisita apenas os que não estão no cache
      const pedidosParaCarregar = pedidos.filter(p => !initialMap[getPedidoKey(p)]);

      if (pedidosParaCarregar.length === 0) {
        setCarregandoResumo(false);
        return;
      }

      // Inicializa com loading os que vão ser carregados
      pedidosParaCarregar.forEach((pedido) => {
        initialMap[getPedidoKey(pedido)] = { loading: true };
      });
      setStatusResumo(initialMap);

      await Promise.all(pedidosParaCarregar.map(async (pedido) => {
        const pedidoKey = getPedidoKey(pedido);
        try {
          const resp = await fetch(`/api/orders/${encodeURIComponent(pedido.orderId)}/desdobramento`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + token,
            },
          });
          const json = await resp.json();
          if (json.ok && Array.isArray(json.resumoPorProduto)) {
            const summary = getSummaryStatus({ desdobramento: json });
            setStatusResumo((prev) => {
              const updated = {
                ...prev,
                [pedidoKey]: summary || getStatusInfo(pedido.orderStatus),
              };
              // Salva em cache (converte para string para sessionStorage)
              const cacheData = {};
              Object.entries(updated).forEach(([key, value]) => {
                cacheData[key] = typeof value === 'string' ? value : value?.texto;
              });
              sessionStorage.setItem('statusResumo', JSON.stringify(cacheData));
              return updated;
            });
          } else {
            setStatusResumo((prev) => ({
              ...prev,
              [pedidoKey]: getStatusInfo(pedido.orderStatus),
            }));
          }
        } catch {
          setStatusResumo((prev) => ({
            ...prev,
            [pedidoKey]: getStatusInfo(pedido.orderStatus),
          }));
        }
      }));

      setCarregandoResumo(false);
    };

    carregarStatusResumo();
  }, [pedidos]);

  // Resetar página quando o filtro mudar
  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroStatus]);

  const mudarPagina = (novaPagina) => {
    if (novaPagina >= 1 && novaPagina <= totalPaginas) {
      setPaginaAtual(novaPagina);
    }
  };

  const handleLogout = () => fazerLogout(navigate);

  const handleVerDetalhes = async (pedido) => {
    setPedidoCarregandoDetalhesId(getPedidoKey(pedido));
    setCarregandoDetalhes(true);
    try {
      const { token } = getAuthSession();
      const response = await fetch('/api/orders/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({
          orderId: pedido.orderId,
        }),
      });
      const data = await response.json();
      if (data.ok && data.pedido) {
        const details = data.pedido;
        const tipo = extractSaleType(details) || extractSaleType(pedido);
        if (tipo === '7' || tipo === 7) {
          try {
            const pedidoKey = getPedidoKey(pedido);
            
            // Tenta carregar do cache com TTL
            const cachedDesdobramento = obterCacheDesdobramento(pedido.orderId);
            
            if (cachedDesdobramento) {
              // Cache válido, mostra direto
              const summary = getSummaryStatus({ desdobramento: cachedDesdobramento });
              if (summary) {
                setStatusResumo((prev) => ({
                  ...prev,
                  [pedidoKey]: summary,
                }));
              }
              setPedidoDetalhado({ ...details, desdobramento: cachedDesdobramento });
            } else {
              // Cache inválido ou expirado, faz requisição
              const respDesdob = await fetch(`/api/orders/${encodeURIComponent(pedido.orderId)}/desdobramento`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': 'Bearer ' + token,
                },
              });
              const jsonDesdob = await respDesdob.json();
              if (jsonDesdob.ok) {
                // Salva no cache
                salvarCacheDesdobramento(pedido.orderId, jsonDesdob);
                
                const summary = getSummaryStatus({ desdobramento: jsonDesdob });
                if (summary) {
                  setStatusResumo((prev) => ({
                    ...prev,
                    [pedidoKey]: summary,
                  }));
                  // Atualiza cache de status também
                  sessionStorage.setItem('statusResumo', JSON.stringify({
                    ...JSON.parse(sessionStorage.getItem('statusResumo') || '{}'),
                    [pedidoKey]: summary === 'CONCLUÍDO' ? 'CONCLUÍDO' : 'PENDENTE'
                  }));
                }
                setPedidoDetalhado({ ...details, desdobramento: jsonDesdob });
              } else {
                console.warn('[PEDIDOS] Desdobramento não disponível:', jsonDesdob.erro);
                setPedidoDetalhado(details);
              }
            }
          } catch (e) {
            console.warn('[PEDIDOS] Falha ao buscar desdobramento:', e.message || e);
            setPedidoDetalhado(details);
          }
        } else {
          setPedidoDetalhado(details);
        }
      } else {
        setPedidoDetalhado({ ...pedido, clienteNome, erro: data.erro || 'Erro ao carregar detalhes' });
      }
    } catch (err) {
      setPedidoDetalhado({ ...pedido, clienteNome, erro: 'Erro ao conectar com o servidor' });
    } finally {
      setCarregandoDetalhes(false);
      setPedidoCarregandoDetalhesId(null);
    }
  };
  const fecharDetalhes    = () => setPedidoDetalhado(null);

  return (
    <div className="pedidos-container">
      <header className="dashboard-header">
        <div className="header-left">
          <Logo size="small" onClick={() => navigate('/dashboard')} />
          <div className="header-text">
            <h1>Pedidos</h1>
            <p className="cliente-info">{clienteNome || 'Carregando...'}</p>
          </div>
        </div>
        <nav className="nav-buttons">
          <button onClick={() => navigate('/dashboard')} className="btn-nav">Dashboard</button>
          <button onClick={handleLogout} className="btn-logout">
            <span className="btn-logout__icon" aria-hidden>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M16 17l5-5-5-5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 19H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            Sair
          </button>
        </nav>
      </header>

      <div className="pedidos-content">
        {/* Filtros */}
        <div className="filtros-section">
          <div className="status-filtro">
            <p className="status-filtro__titulo">Filtrar por status</p>

            <div className="status-filtro__lista">
              <button
                className={`status-filtro__botao ${filtroStatus === 'todos' ? 'status-filtro__botao--ativo' : ''}`}
                onClick={() => setFiltroStatus('todos')}
              >
                <span className="status-filtro__icone" aria-hidden>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="M7 7h6v6H7z" strokeLinecap="round" strokeLinejoin="round" />
                    <rect x="3" y="3" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Todos
                <span className="status-filtro__contagem">{pedidos.length}</span>
              </button>

              <button
                className={`status-filtro__botao ${filtroStatus === 'M' ? 'status-filtro__botao--ativo' : ''}`}
                onClick={() => setFiltroStatus('M')}
              >
                <span className="status-filtro__icone status-filtro__icone--pendente" aria-hidden>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 7v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Pendente
                <span className="status-filtro__contagem">{pedidos.filter(p => getPedidoEffectiveStatus(p).texto === 'PENDENTE').length}</span>
              </button>

              <button
                className={`status-filtro__botao ${filtroStatus === 'F' ? 'status-filtro__botao--ativo' : ''}`}
                onClick={() => setFiltroStatus('F')}
              >
                <span className="status-filtro__icone status-filtro__icone--concluido" aria-hidden>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Concluído
                <span className="status-filtro__contagem">{pedidos.filter(p => getPedidoEffectiveStatus(p).texto === 'CONCLUÍDO').length}</span>
              </button>

              <button
                className={`status-filtro__botao ${filtroStatus === 'C' ? 'status-filtro__botao--ativo' : ''}`}
                onClick={() => setFiltroStatus('C')}
              >
                <span className="status-filtro__icone status-filtro__icone--cancelado" aria-hidden>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Cancelados
                <span className="status-filtro__contagem">{pedidos.filter(p => String(p.orderStatus) === 'C').length}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="pedidos-toolbar">
          <div className="pedidos-busca">
            <span className="pedidos-busca__icone" aria-hidden>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6">
                <circle cx="11" cy="11" r="6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 21l-4.35-4.35" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <input
              type="text"
              className="pedidos-busca__input"
              placeholder="Buscar por número do pedido ou nota fiscal"
              value={buscaTexto}
              onChange={(e) => setBuscaTexto(e.target.value)}
            />
          </div>

          <select
            className="pedidos-ordenar"
            value={ordenacao}
            onChange={(e) => setOrdenacao(e.target.value)}
          >
            <option value="mais-recentes">Mais recentes</option>
            <option value="maior-valor">Maior valor</option>
            <option value="menor-valor">Menor valor</option>
          </select>
        </div>

        {/* Lista */}
        {carregando ? (
          <div className="sem-pedidos"><p>Carregando pedidos...</p></div>
        ) : pedidosFiltrados.length === 0 ? (
          <div className="sem-pedidos">
            <p>Nenhum pedido encontrado.</p>
          </div>
        ) : (
          <>
            <div className="pedidos-lista">
              {pedidosDaPagina.map(pedido => {
                const status = getPedidoEffectiveStatus(pedido);
                const statusClasse = status ? status.classe : 'status-loading';
                const statusTexto = status ? status.texto : 'Carregando...';
                const statusCardClass = status
                  ? (status.loading ? 'carregando' : status.texto === 'CONCLUÍDO' ? 'concluido' : status.texto === 'PENDENTE' ? 'pendente' : pedido.orderStatus.toLowerCase())
                  : 'carregando';
                const statusBadgeType = statusTexto === 'CONCLUÍDO'
                  ? 'concluido'
                  : statusTexto === 'PENDENTE'
                    ? 'pendente'
                    : statusTexto === 'CANCELADO'
                      ? 'cancelado'
                      : statusTexto === 'CARREGANDO...'
                        ? 'carregando'
                        : 'pendente';
                return (
                  <div key={pedido.orderId} className={`pedido-card pedido-card--status-${statusCardClass}`}>
                    <div className="pedido-card__header">
                      <h3 className="pedido-card__numero">Pedido #{pedido.orderId}</h3>
                      <span className={`pedido-card__badge pedido-card__badge--${statusBadgeType}`}>
                        {statusTexto}
                      </span>
                    </div>
                    <div className="pedido-card__valor-box">
                      <p className="pedido-card__valor-label">Valor total</p>
                      <p className="pedido-card__valor">{formatarMoeda(pedido.totalValue)}</p>
                    </div>
                    <table className="pedido-card__detalhes">
                      <tbody>
                        <tr>
                          <td className="cliente">Cliente</td>
                          <td>{clienteNome}</td>
                        </tr>
                        <tr>
                          <td>Data</td>
                          <td>{formatarData(pedido.createData)}</td>
                        </tr>
                        {pedido.notaFiscal && (
                          <tr>
                            <td>Nota fiscal</td>
                            <td>{pedido.notaFiscal}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <button
                      className="pedido-card__botao"
                      onClick={() => handleVerDetalhes(pedido)}
                      disabled={pedidoCarregandoDetalhesId === getPedidoKey(pedido)}
                    >
                      {pedidoCarregandoDetalhesId === getPedidoKey(pedido) ? 'Carregando detalhes...' : 'Ver detalhes'}
                    </button>
                  </div>
              );
            })}
            </div>

            {/* Controles de Paginação */}
            {totalPaginas > 1 && (
              <div className="paginacao-container">
                <div className="paginacao-info">
                  <span>Página {paginaAtual} de {totalPaginas}</span>
                  <span>({pedidosFiltrados.length} pedidos)</span>
                </div>
                <div className="paginacao-botoes">
                  <button 
                    className="btn-paginacao btn-anterior"
                    onClick={() => mudarPagina(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                  >
                    ← Anterior
                  </button>
                  <div className="paginacao-numeros">
                    {(() => {
                      const paginasVisiveis = [];
                      const maxVisiveis = 5; // Máximo de botões visíveis (mais compacto)
                      
                      if (totalPaginas <= maxVisiveis) {
                        // Se tiver poucas páginas, mostra todas
                        for (let i = 1; i <= totalPaginas; i++) {
                          paginasVisiveis.push(i);
                        }
                      } else {
                        // Lógica compacta - mostra menos páginas
                        if (paginaAtual <= 3) {
                          // Início: 1, 2, 3, ..., última
                          for (let i = 1; i <= 3; i++) {
                            paginasVisiveis.push(i);
                          }
                          paginasVisiveis.push('...');
                          paginasVisiveis.push(totalPaginas);
                        } else if (paginaAtual >= totalPaginas - 2) {
                          // Final: 1, ..., penúltima-1, penúltima, última
                          paginasVisiveis.push(1);
                          paginasVisiveis.push('...');
                          for (let i = totalPaginas - 2; i <= totalPaginas; i++) {
                            paginasVisiveis.push(i);
                          }
                        } else {
                          // Meio: 1, ..., atual-1, atual, atual+1, ..., última
                          paginasVisiveis.push(1);
                          paginasVisiveis.push('...');
                          paginasVisiveis.push(paginaAtual - 1);
                          paginasVisiveis.push(paginaAtual);
                          paginasVisiveis.push(paginaAtual + 1);
                          paginasVisiveis.push('...');
                          paginasVisiveis.push(totalPaginas);
                        }
                      }
                      
                      return paginasVisiveis.map((item, index) => {
                        if (item === '...') {
                          return <span key={`ellipsis-${index}`} className="paginacao-ellipsis">...</span>;
                        }
                        return (
                          <button
                            key={item}
                            className={`btn-pagina ${paginaAtual === item ? 'ativa' : ''}`}
                            onClick={() => mudarPagina(item)}
                          >
                            {item}
                          </button>
                        );
                      });
                    })()}
                  </div>
                  <button 
                    className="btn-paginacao btn-proximo"
                    onClick={() => mudarPagina(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                  >
                    Próximo →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {pedidoDesdobramento && (
        <Desdobramento
          orderId={pedidoDesdobramento}
          onClose={() => setPedidoDesdobramento(null)}
        />
      )}

      {/* Modal de Detalhes */}
      {pedidoDetalhado && (
        <div className="modal-overlay" onClick={fecharDetalhes}>
          <div className="modal-detalhes modal-detalhes-completo" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalhes do Pedido</h2>
              <button className="modal-fechar" onClick={fecharDetalhes}>×</button>
            </div>
            <div className="modal-body">
              {carregandoDetalhes ? (
                <div className="modal-loading">
                  <p>Carregando detalhes...</p>
                </div>
              ) : pedidoDetalhado.erro ? (
                <div className="modal-erro">
                  <p>{pedidoDetalhado.erro}</p>
                </div>
              ) : (
                <>
                  {/* Cabeçalho do pedido */}
                  <div className="resultado-header">
                    <div className="resultado-id">
                      <span className="resultado-label">Pedido</span>
                      <span className="resultado-numero">#{pedidoDetalhado.idPedido || pedidoDetalhado.orderId}</span>
                    </div>
                    <span
                      className="resultado-status"
                      style={{
                        color: (getSummaryStatus(pedidoDetalhado) || getStatus(pedidoDetalhado.orderStatus)).cor,
                        backgroundColor: (getSummaryStatus(pedidoDetalhado) || getStatus(pedidoDetalhado.orderStatus)).bg,
                      }}
                    >
                      {(getSummaryStatus(pedidoDetalhado) || getStatus(pedidoDetalhado.orderStatus)).texto}
                    </span>
                  </div>

                  {/* Nota Fiscal */}
                  {pedidoDetalhado.numeroNotaFiscal && (
                    <div className="resultado-nf">
                      <span className="nf-icon">🧾</span>
                      <span className="nf-label">Nota Fiscal:</span>
                      <span className="nf-numero">NF {pedidoDetalhado.numeroNotaFiscal}</span>
                    </div>
                  )}

                  {/* Grid de informações */}
                  <div className="resultado-grid">
                    <div className="resultado-card">
                      <h3>📦 Dados do Pedido</h3>
                      <table className="info-table">
                        <tbody>
                          <tr><td>Número</td><td>#{pedidoDetalhado.idPedido || pedidoDetalhado.orderId}</td></tr>
                          <tr><td>Status</td><td style={{ color: getStatus(pedidoDetalhado.orderStatus).cor, fontWeight: 700 }}>{getStatus(pedidoDetalhado.orderStatus).texto}</td></tr>
                          <tr><td>Data</td><td>{formatarData(pedidoDetalhado.createData)}</td></tr>
                          <tr><td>Valor Total</td><td>{formatarMoeda(pedidoDetalhado.valorTotal || pedidoDetalhado.totalValue)}</td></tr>
                          <tr><td>Plano de Pagamento</td><td>{getPaymentPlan(pedidoDetalhado.paymentPlanId)}</td></tr>
                          <tr><td>Tipo de Venda</td><td>{SALE_TYPE_MAP[pedidoDetalhado.saleType] || pedidoDetalhado.saleType || '—'}</td></tr>
                          <tr><td>Origem</td><td>{SALE_ORIGIN_MAP[pedidoDetalhado.saleOrigin] || pedidoDetalhado.saleOrigin || '—'}</td></tr>
                          {pedidoDetalhado.numeroNotaFiscal && <tr><td>Nota Fiscal</td><td>{pedidoDetalhado.numeroNotaFiscal}</td></tr>}
                          {pedidoDetalhado.observation && <tr><td>Observação</td><td>{pedidoDetalhado.observation}</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Itens do Pedido */}
                  {pedidoDetalhado.itens && pedidoDetalhado.itens.length > 0 && (
                    <div className="resultado-itens">
                      <h3>🛒 Itens do Pedido</h3>
                      <div className="itens-table-wrapper">
                        <table className="itens-table">
                          <thead>
                            <tr>
                              <th>Produto</th>
                              <th>Qtd</th>
                              <th>Preço Unit.</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pedidoDetalhado.itens.map((item, i) => (
                              <tr key={i}>
                                <td className="produto-nome">
                                  {item.productName || '—'}
                                  {item.productId && (
                                    <small className="produto-id"> (#{item.productId})</small>
                                  )}
                                </td>
                                <td>{item.quantity ?? '—'}</td>
                                <td>{formatarMoeda(item.sellPrice)}</td>
                                <td>{formatarMoeda((item.sellPrice || 0) * (item.quantity || 1))}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan="3" style={{ textAlign: 'right', fontWeight: 700 }}>Total do Pedido</td>
                              <td style={{ fontWeight: 700, color: '#10b981' }}>
                                {formatarMoeda(pedidoDetalhado.valorTotal || pedidoDetalhado.totalValue)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {pedidoDetalhado.desdobramento && pedidoDetalhado.desdobramento.resumoPorProduto && pedidoDetalhado.desdobramento.resumoPorProduto.length > 0 && (
                    <div className="resultado-card" style={{ marginTop: '24px' }}>
                      <h3>🔗 Resumo por produto</h3>
                      {pedidoDetalhado.desdobramento.mensagem && (
                        <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                          {pedidoDetalhado.desdobramento.mensagem}
                        </p>
                      )}
                      <div className="itens-table-wrapper">
                        <table className="itens-table">
                          <thead>
                            <tr>
                              <th>Produto</th>
                              <th>Qtd. Pedida</th>
                              <th>Qtd. Desdobrada</th>
                              <th>Qtd. Restante</th>
                              <th>% Entregue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...pedidoDetalhado.desdobramento.resumoPorProduto]
                              .sort((a, b) => {
                                const nameA = String(a.productName || a.productSKUERPReferenceKey || a.productId || '').trim();
                                const nameB = String(b.productName || b.productSKUERPReferenceKey || b.productId || '').trim();
                                return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
                              })
                              .map((item, i) => {
                              const pedida = Number(item.qtdPedida || 0);
                              const desdobrada = Number(item.qtdDesdobrada || 0);
                              const percentual = pedida > 0 ? Math.min(100, Math.round((desdobrada / pedida) * 100)) : 0;
                              const restante = Number(item.qtdRestante || 0);
                              return (
                                <tr key={item.productId || item.productSKUERPReferenceKey || i}>
                                  <td>{item.productName || item.productSKUERPReferenceKey || item.productId || '—'}</td>
                                  <td>{item.qtdPedida ?? '—'}</td>
                                  <td>{item.qtdDesdobrada ?? '—'}</td>
                                  <td style={{ color: restante > 0 ? '#b45309' : '#111', fontWeight: restante > 0 ? 700 : 400 }}>
                                    {item.qtdRestante ?? '—'}
                                  </td>
                                  <td>
                                    <div className="progress-row">
                                      <div className="progress-bar">
                                        <div className="progress-bar__fill" style={{ width: `${percentual}%` }}></div>
                                        <span className="progress-bar__label">{percentual}%</span>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {pedidoDetalhado.desdobramento.pedidosFilhos && pedidoDetalhado.desdobramento.pedidosFilhos.length > 0 && (
                        <div style={{ marginTop: '28px' }}>
                          <h3>📦 Pedidos filhos / Remessa</h3>
                          <div className="desdobramento-botoes-filhos" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                            {[...pedidoDetalhado.desdobramento.pedidosFilhos]
                              .sort((a, b) => {
                                const idA = String(a.idPedido || a.orderId || '').trim();
                                const idB = String(b.idPedido || b.orderId || '').trim();
                                return idA.localeCompare(idB, 'pt-BR', { numeric: true, sensitivity: 'base' });
                              })
                              .map((filho) => (
                              <button
                                key={filho.idPedido}
                                type="button"
                                className={`btn-pedido-filho ${pedidoFilhoSelecionado?.idPedido === filho.idPedido ? 'ativo' : ''}`}
                                onClick={() => setPedidoFilhoSelecionado(prev => prev?.idPedido === filho.idPedido ? null : filho)}
                              >
                                Pedido {filho.idPedido}
                              </button>
                            ))}
                          </div>

                          {pedidoFilhoSelecionado && (
                            <div className="desdobramento-detalhes-filho" style={{ marginTop: '18px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#fff' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <div>
                                  <strong>Pedido filho:</strong> {pedidoFilhoSelecionado.idPedido}
                                </div>
                                <div style={{ textAlign: 'right', fontSize: '14px', fontFamily: 'inherit' }}>
                                  <div style={{ color: '#1a1a1a', fontWeight: 600 }}><strong>Valor total:</strong></div>
                                  <div style={{ color: '#10b981', fontWeight: 700 }}>{formatarMoeda(pedidoFilhoSelecionado.valorTotal || pedidoFilhoSelecionado.totalValue || 0)}</div>
                                </div>
                              </div>
                              {pedidoFilhoSelecionado.itens && pedidoFilhoSelecionado.itens.length > 0 ? (
                                <div className="itens-table-wrapper" style={{ marginTop: '14px' }}>
                                  <table className="itens-table">
                                    <thead>
                                      <tr>
                                        <th>Produto</th>
                                        <th>Qtd</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {pedidoFilhoSelecionado.itens.map((item, index) => (
                                        <tr key={`${pedidoFilhoSelecionado.idPedido}-${index}`}>
                                          <td className="produto-nome">
                                            {item.productName || item.productSKUERPReferenceKey || item.productId || '—'}
                                          </td>
                                          <td>{item.quantity ?? '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p style={{ marginTop: '12px', color: '#6b7280' }}>Nenhum item disponível para este pedido filho.</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pedidos;
